/**
 * Copyright (c) 2021-22 Amway
 * @author Mohit Chunawala
 */

import React from "react";
import QuantityButton from "../quantity-button";
import Button from "../button";
import {
  addCartEntries,
  deductCartEntriesCount,
  getCartEntries,
  getOrCreateCart,
  deleteCartEntries,
  cartMergingCall,
} from "../../../services/commerceServices/cartServices";
import {
  getLanguage,
  setAccessToken,
} from "../../../services/commerceServices/accessToken";
import { withOktaAuth } from "@okta/okta-react";
import { validateOktaSession } from "../../../utils/validateOktaSession";
import ErrorScreen from "../errors/error-screen";
import MessageStrip from "../message-strip";
import Loader from "../loader";

// Interface for CartComponent State
interface CartComponentState {
  closeCart: boolean;
  cartProductsList: any;
  quantity: number;
  successfullyDeleted: boolean;
  cartLoader: boolean;
}

/**
 * Cart component.
 *
 * @namespace CartComponent
 */

var x: any;
var increaseClickCount: any = 0;
class CartComponent extends React.Component<any, CartComponentState> {
  node: any;
  constructor(props: any) {
    super(props);
    this.state = {
      closeCart: false,
      cartProductsList: this.props.productList || [],
      quantity: 0,
      successfullyDeleted: false,
      cartLoader: false,
    };
  }

  private initializeCart = async () => {
    // prevent duplicate inits while already loading
    if (this.state.cartLoader) return;

    this.setState({ cartLoader: true, closeCart: false });

    try {
      if (!localStorage.getItem("commerce_token_storage")) {
        await setAccessToken();
      }

      await getOrCreateCart();

      if (
        localStorage.getItem("cartId") &&
        localStorage.getItem("okta-token-storage")
      ) {
        try {
        await cartMergingCall();
      } catch (e) {
        console.warn("Cart merge failed, continuing with logged-in cart", e);
      }
    }

      await validateOktaSession(this.props.oktaAuth);

      // this sets cartProductsList and sets cartLoader:false
      await this.getCartProductsList();
    } catch (e) {
      this.setState({ cartLoader: false });
    }
  };

  componentDidMount = async () => {
    if (!localStorage.getItem("commerce_token_storage")) {
      await setAccessToken();
    }
    await validateOktaSession(this.props.oktaAuth);

    if (this.props.show) {
      document.removeEventListener("click", this.handleOutsideClick, true);
      document.removeEventListener("keydown", this.handleKeyDown, true);

      document.addEventListener("click", this.handleOutsideClick, true);
      document.addEventListener("keydown", this.handleKeyDown, true);
      await this.initializeCart();
    }
  };

  getCartProductsList = async () => {
    try {
      const response: any = await getCartEntries();
      const entries = response?.entries ?? [];
      this.setState({
        cartProductsList: entries,
        cartLoader: false,
      });
    } catch (e) {
      this.setState({ cartLoader: false });
    }
  };

  componentDidUpdate = async (prevProps: any) => {
    const opened = !prevProps.show && this.props.show;
    const closed = prevProps.show && !this.props.show;

    if (opened) {
      document.removeEventListener("click", this.handleOutsideClick, true);
      document.removeEventListener("keydown", this.handleKeyDown, true);

      document.addEventListener("click", this.handleOutsideClick, true);
      document.addEventListener("keydown", this.handleKeyDown, true);

      await this.initializeCart();
    }

    if (closed) {
      document.removeEventListener("click", this.handleOutsideClick, true);
      document.removeEventListener("keydown", this.handleKeyDown, true);
    }
  };

  componentWillUnmount() {
    document.removeEventListener("click", this.handleOutsideClick, true);
    document.removeEventListener("keydown", this.handleKeyDown, true);
  }

  handleKeyDown = (e: any) => {
    if (e.key === "Escape") {
      this.handleOutsideClick(e);
    }
  };

  handleOutsideClick = (e: any) => {
    // ignore clicks on the component itself
    if (this.node) {
      if (this.node.contains(e.target)) {
        return;
      }
      this.handleClose();
    }
  };

  handleClose = (e?: any) => {
    this.setState(
      {
        closeCart: true,
      },
      () => {
        this.props.handleClose();
      },
    );
  };
  increaseQuantity = (productCode: number) => {
    this.setState(
      {
        quantity: this.state.quantity + 1,
      },
      () => {
        clearInterval(x);
        x = setInterval(() => {
          addCartEntries(productCode, this.state.quantity).then(() => {
            this.getCartProductsList();
            clearInterval(x);
            this.setState({
              quantity: 0,
            });
          });
        }, 1000);
      },
    );
  };
  decreaseQuantity = (
    productCode: number,
    quantity: number,
    entryNumber: number,
  ) => {
    if (quantity && quantity > 1) {
      increaseClickCount = increaseClickCount + 1;
      clearInterval(x);
      x = setInterval(() => {
        this.setState(
          {
            quantity: quantity - increaseClickCount,
          },
          () => {
            deductCartEntriesCount(
              productCode,
              this.state.quantity,
              entryNumber,
            ).then(() => {
              this.getCartProductsList();
              clearInterval(x);
              increaseClickCount = 0;
              this.setState({
                quantity: 0,
              });
            });
          },
        );
      }, 1000);
    }
  };

  deleteProductFromCart = (entryNumber: number, e?: any) => {
    deleteCartEntries(entryNumber).then(() => {
      this.getCartProductsList();
      this.setState(
        {
          successfullyDeleted: true,
        },
        () => {
          setTimeout(() => this.setState({ successfullyDeleted: false }), 2000);
        },
      );
    });
  };

  redirectToProducts = () => {
    this.props.history.push("/results");
  };

  handleProceedToCheckOut = () => {
    if (!localStorage.getItem("okta-token-storage")) {
      window.open(
        `${process.env.REACT_APP_REDIRECT_CART_URL}`?.replaceAll(
          "language",
          getLanguage() || "",
        ),
        "_self",
      );
    } else {
      window.open(
        `${process.env.REACT_APP_REDIRECT_CHECKOUT_URL}`?.replaceAll(
          "language",
          getLanguage() || "",
        ),
        "_self",
      );
    }
  };

  render() {
    const { cartProductsList, successfullyDeleted, cartLoader } = this.state;
    const splitedStr = this.props.history.location.pathname.split("/");
    const pathName = splitedStr && splitedStr.slice(1).pop();
    let subTotal = cartProductsList.reduce(
      (p1: any, p2: any) => (p1 = p1 + p2.totalPrice.value),
      0,
    );
    // let amPerksPoints = cartProductsList.reduce(
    //   (t1: any, t2: any) => (t1 = t1 + t2.totalLoyaltyPoints),
    //   0
    // );
    let totalCartQuantity = cartProductsList.reduce(
      (q1: any, q2: any) => (q1 = q1 + q2.quantity),
      0,
    );
    let animationClass = "inner-wrapper hide";
    if (this.state.closeCart) {
      animationClass = "inner-wrapper animated slideInLeft close-cart";
    }
    if (this.props.show) {
      animationClass = "inner-wrapper animated slideInRight";
    }
    return (
      <div
        className="cart-container"
        tabIndex={-1}
        role="slider"
        aria-valuenow={1}
        aria-labelledby="myDialog"
      >
        {/* Add to Cart Success Message Start */}
        {successfullyDeleted ? (
          <MessageStrip
            messageText={this.props.t("common.user_deleted_product_from_cart")}
          />
        ) : null}
        {/* Add to Cart Success Message Ends */}
        <div className={this.props.show ? "overlay" : "overlay hide"}></div>
        <div
          className={animationClass}
          ref={(node) => {
            this.node = node;
          }}
        >
          {/* Cart header Start */}
          <div className="cart-header">
            <div className="heading">
              <div className="label" id="myDialog">
                {this.props.t("common.my_cart")}
              </div>
              <div
                className={
                  totalCartQuantity && totalCartQuantity > 0 ? "items" : "hide"
                }
              >{`( ${totalCartQuantity} ${
                totalCartQuantity > 1
                  ? this.props.t("common.items")
                  : this.props.t("common.item")
              } )`}</div>
            </div>
            <span
              tabIndex={0}
              role="button"
              aria-label="Cart close"
              className="icon cross-icon"
              onClick={this.handleClose}
              onKeyPress={(e: any) => {
                if (e.key === "Enter" || e.key === " ") {
                  this.handleClose(e);
                }
              }}
            ></span>
          </div>
          {/* Cart header Ends */}

          {/* Cart middle area Start */}
          {cartLoader ? (
            <Loader loaderClassName={"loader-wrapper align-middle"} />
          ) : (
            <>
              <div className="cart-middle-area">
                {/* Product container Start */}
                {cartProductsList && cartProductsList.length > 0 ? (
                  cartProductsList.map((data: any) => (
                    <div className="product-container">
                      <span
                        tabIndex={0}
                        aria-label="Delete item from cart"
                        role="button"
                        className="icon delete-icon"
                        onClick={() =>
                          this.deleteProductFromCart(data && data.entryNumber)
                        }
                        onKeyPress={(e: any) => {
                          if (e.key === "Enter" || e.key === " ") {
                            this.deleteProductFromCart(
                              data && data.entryNumber,
                              e,
                            );
                          }
                        }}
                      ></span>
                      <div className="product-details">
                        <div className="left-wrapper">
                          {data &&
                            data.product &&
                            data.product.images &&
                            data.product.images.length > 0 &&
                            data.product.images
                              .filter((item: any) => item.format === "cartIcon")
                              .map((img: any) => (
                                <div className="product-img icon">
                                  <img
                                    src={`${
                                      process.env.REACT_APP_IMGAE_BASE_URL
                                    }/${img && img.publicUrl}`}
                                    alt="product"
                                  />
                                </div>
                              ))}
                        </div>
                        <div className="right-wrapper">
                          <div className="product-name">
                            {data && data.product && data.product.name}
                          </div>
                          <div className="bottom-wrapper">
                            <div className="add-to-cart-wrapper">
                              <QuantityButton
                                name={data && data.quantity}
                                className="app-btn btn-quantity"
                                handleDecrease={() =>
                                  this.decreaseQuantity(
                                    data && data.product && data.product.code,
                                    data && data.quantity,
                                    data && data.entryNumber,
                                  )
                                }
                                handleIncrease={() =>
                                  this.increaseQuantity(
                                    data && data.product && data.product.code,
                                  )
                                }
                              />
                            </div>
                            <div className="price">
                              {data &&
                                data.totalPrice &&
                                data.totalPrice.formattedValue}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <ErrorScreen
                    errorMainClassName={"error-screen-container"}
                    errorTitle={this.props.t("error.add_recommendations")}
                    errorBtnName={this.props.t("common.take_me_to_products")}
                    handleErrorBtn={() => this.redirectToProducts()}
                    // withBtn={
                    //   pathName === "results" || pathName === "summary"
                    //     ? true
                    //     : false
                    // }
                  />
                )}
                {/* Product container Ends */}
              </div>
              {/* Cart middle area Ends */}

              {/* Cart bottom Section Start */}
              {cartProductsList && cartProductsList.length > 0 ? (
                <div className="cart-footer-wrapper">
                  <div className="details-wrapper">
                    <div className="left-wrapper">
                      <div className="subtotal-wrapper">
                        <div className="label">
                          {this.props.t("common.subtotal")}
                        </div>
                        <div className="notes">
                          {this.props.t(
                            "common.excluding_taxes_and_other_charges",
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="checkout-button">
                    <Button
                      name={this.props.t("common.checkout")}
                      className="app-btn"
                      onButtonClick={() => this.handleProceedToCheckOut()}
                    />
                  </div>
                </div>
              ) : null}
              {/* Cart bottom Section Ends */}
            </>
          )}
        </div>
      </div>
    );
  }
}

export default withOktaAuth(CartComponent);
